import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "../store";
import axios from 'axios';

// Redux
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // JavaScript months are 0-based.
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

// Example usage
const formattedDate = formatDate("Mon Mar 04 2024 00:00:00 GMT+0000 (Greenwich Mean Time)");
console.log(formattedDate); // Outputs: "04-03-2024"

// ----------------------------------------------------------------------

let initialState = {
  configuration: {},
  isConfigLoading: false,

  showSettingsSuccess: false,
  error: '',

  // Email routing rules
  emailRoutingRules: [],
  emailRoutingIndividuals: [],
  isEmailRoutingLoading: false,
  // New email routing data per updated API
  emailRoutingEvents: [],
  emailRoutingAliases: [],
  // Employees export status
  employeesExportStatus: 'idle',
  employeesExportError: null,

  // Levels & Rates
  levels: [],
  isLevelsLoading: false,
  levelsError: null,

  // Jobs (for display below levels)
  jobs: [],
  isJobsLoading: false,
  jobsError: null,
};

export const settingsSlice = createSlice({
  name: "opportunities",
  initialState,
  reducers: {

    setConfiguration(state, action) {
      state.configuration = action.payload;
      state.isNewOppLoading = false;
      state.isConfigLoading = false;
    },

    startConfigurationLoading(state) {
      state.isConfigLoading = true;
    },

    setShowSettingsSuccess(state, action) {
      state.showSettingsSuccess = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isConfigLoading = false;
      state.error = action.payload;
    },

    // Email routing reducers
    startEmailRoutingLoading(state) {
      state.isEmailRoutingLoading = true;
    },
    setEmailRoutingRules(state, action) {
      state.emailRoutingRules = action.payload || [];
      state.isEmailRoutingLoading = false;
    },
    setEmailRoutingIndividuals(state, action) {
      state.emailRoutingIndividuals = action.payload || [];
    },
    setEmailRoutingEvents(state, action) {
      state.emailRoutingEvents = action.payload || [];
    },
    setEmailRoutingAliases(state, action) {
      state.emailRoutingAliases = action.payload || [];
    },

    // Employees export reducers
    setEmployeesExportStatus(state, action) {
      state.employeesExportStatus = action.payload;
    },
    setEmployeesExportError(state, action) {
      state.employeesExportError = action.payload;
    },

    // Levels & Rates reducers
    startLevelsLoading(state) {
      state.isLevelsLoading = true;
      state.levelsError = null;
    },
    setLevels(state, action) {
      state.levels = Array.isArray(action.payload) ? action.payload : [];
      state.isLevelsLoading = false;
    },
    setLevelsError(state, action) {
      state.levelsError = action.payload || 'Unknown error';
      state.isLevelsLoading = false;
    },

    // Jobs reducers
    startJobsLoading(state) {
      state.isJobsLoading = true;
      state.jobsError = null;
    },
    setJobs(state, action) {
      state.jobs = Array.isArray(action.payload) ? action.payload : [];
      state.isJobsLoading = false;
    },
    setJobsError(state, action) {
      state.jobsError = action.payload || 'Unknown error';
      state.isJobsLoading = false;
    },

  },

});

export const {
  setinputValue,
  setCurrentPage,
  setLastStatus,
  setSortColumn,
  setSortDirection,
  setSearchTerm,
  setStatusFilter,
  setShowSettingsSuccess,
  hasError,
  startEmailRoutingLoading,
  setEmailRoutingRules,
  setEmailRoutingIndividuals,
  setEmailRoutingEvents,
  setEmailRoutingAliases,
  setEmployeesExportStatus,
  setEmployeesExportError,
  startLevelsLoading,
  setLevels,
  setLevelsError,
  startJobsLoading,
  setJobs,
  setJobsError,
} = settingsSlice.actions;

export function getConfiguration() {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    dispatch(settingsSlice.actions.startConfigurationLoading());
    try {
      const response = await axios.get(`${API_URL}/api/settings/stdtemplate/configuration`);
      dispatch(settingsSlice.actions.setConfiguration(response.data.data));
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error));
    }
  };
}

export function updateSTDTemplateResponsibilities(formData) {
  const API_URL = process.env.REACT_APP_API_URL;

  return async () => {
    try {
      const response = await axios.post(`${API_URL}/api/settings/stdtemplate/update`,
        formData, {
        headers: {
          "Content-Type": "application/json",
        },
      }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      dispatch(getConfiguration());

      return response;
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error.response.data.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

// Email routing API calls
export function getEmailRouting() {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    dispatch(settingsSlice.actions.startEmailRoutingLoading());
    try {
      const response = await axios.get(`${API_URL}/api/settings/email-routing/configuration`);
      // Support new response: { data: { aliases: [], events: [], rules: [] } }
      const payload = response?.data?.data ?? response?.data ?? {};
      const aliases = Array.isArray(payload.aliases) ? payload.aliases : [];
      const events = Array.isArray(payload.events) ? payload.events : [];
      const rules = Array.isArray(payload.rules) ? payload.rules : (Array.isArray(payload) ? payload : []);
      // Older shape support
      const individuals = Array.isArray(payload.individuals) ? payload.individuals : [];
      dispatch(settingsSlice.actions.setEmailRoutingAliases(aliases));
      dispatch(settingsSlice.actions.setEmailRoutingEvents(events));
      dispatch(settingsSlice.actions.setEmailRoutingIndividuals(individuals));
      dispatch(settingsSlice.actions.setEmailRoutingRules(rules));
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setEmailRoutingRules([]));
    }
  };
}

export function updateEmailRouting(rules) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      // Normalize outgoing payload to API shape: { rules: [ { event_id, recipients: [{recipient_type, recipient_value}] } ] }
      const normalizedRules = (Array.isArray(rules) ? rules : []).map((r) => {
        const eventId = r.event_id ?? r.eventId ?? r.event_id_fk ?? r.event?.id ?? r.eventIdFk ?? r.eventId_fk;
        const recipients = (r.recipients || []).map((rec) => {
          // Accept formats: {type, value,label} or strings like 'role:Admin' or 'email:test@x.com'
          if (typeof rec === 'string') {
            if (rec.startsWith('role:')) return { recipient_type: 'role', recipient_value: rec.slice('role:'.length) };
            if (rec.startsWith('user:')) return { recipient_type: 'user', recipient_value: rec.slice('user:'.length) };
            if (rec.startsWith('email:')) return { recipient_type: 'email', recipient_value: rec.slice('email:'.length) };
            if (rec.startsWith('special:dynamic_user')) return { recipient_type: 'special', recipient_value: 'dynamic_user' };
            if (rec.includes('@')) return { recipient_type: 'email', recipient_value: rec };
            return { recipient_type: 'role', recipient_value: rec };
          }
          const type = rec.type || rec.recipient_type;
          const value = rec.value || rec.recipient_value || rec.label;
          if ((type === 'special' || value === 'special:dynamic_user') && (value?.includes('dynamic_user') || rec.value === 'special:dynamic_user')) {
            return { recipient_type: 'special', recipient_value: 'dynamic_user' };
          }
          if (type === 'role' && value?.startsWith('role:')) return { recipient_type: 'role', recipient_value: value.slice('role:'.length) };
          if (type === 'user' && value?.startsWith('user:')) return { recipient_type: 'user', recipient_value: value.slice('user:'.length) };
          if (type === 'email' && value?.startsWith('email:')) return { recipient_type: 'email', recipient_value: value.slice('email:'.length) };
          if (type) return { recipient_type: type, recipient_value: value };
          // Fallbacks
          if (value && typeof value === 'string' && value.includes('@')) return { recipient_type: 'email', recipient_value: value };
          return { recipient_type: 'role', recipient_value: value };
        });
        return { event_id: eventId ?? r.event_id, recipients };
      });

      const response = await axios.post(
        `${API_URL}/api/settings/email-routing/update`,
        { rules: normalizedRules },
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      dispatch(getEmailRouting());
      return response;
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

// Export employees CSV
export function exportEmployees() {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      dispatch(settingsSlice.actions.setEmployeesExportStatus('loading'));
      const response = await axios.get(`${API_URL}/api/export/employees`, { responseType: 'blob' });
      const file = new Blob([response.data], { type: 'text/csv' });
      const fileURL = URL.createObjectURL(file);
      const tempLink = document.createElement('a');
      tempLink.href = fileURL;
      tempLink.setAttribute('download', 'Employees.csv');
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      URL.revokeObjectURL(fileURL);
      dispatch(settingsSlice.actions.setEmployeesExportStatus('succeeded'));
    } catch (error) {
      console.error('Error exporting employees:', error);
      dispatch(settingsSlice.actions.setEmployeesExportError(error?.message || 'Export failed'));
      dispatch(settingsSlice.actions.setEmployeesExportStatus('failed'));
    }
  };
}

// Levels & Rates API calls
export function getLevels() {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      dispatch(settingsSlice.actions.startLevelsLoading());
      const response = await axios.get(`${API_URL}/api/settings/levels`);
      // Expecting { data: [...] } or [...]
      const payload = response?.data?.data ?? response?.data ?? [];
      dispatch(settingsSlice.actions.setLevels(Array.isArray(payload) ? payload : []));
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.setLevelsError(error?.response?.data?.message || error.message));
    }
  };
}

export function upsertLevels(levels) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      const normalized = (Array.isArray(levels) ? levels : []).map((l) => ({
        id: l.id ?? l.level_id ?? undefined,
        level: l.level ?? l.name ?? '',
        salary: Number(l.salary ?? 0),
        internal_rate: Number(l.internal_rate ?? l.internalRate ?? 0),
        external_rate: Number(l.external_rate ?? l.externalRate ?? 0),
      }));
      await axios.post(
        `${API_URL}/api/settings/levels/upsert`,
        { levels: normalized },
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      // Refresh
      dispatch(getLevels());
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error?.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

// Jobs API calls (read-only list to display beneath levels)
export function getJobs() {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      dispatch(settingsSlice.actions.startJobsLoading());
      const response = await axios.get(`${API_URL}/api/settings/jobs`);
      const payload = response?.data?.data ?? response?.data ?? [];
      // Normalize minimal fields we display
      const normalized = (Array.isArray(payload) ? payload : []).map((j) => ({
        id: j.id ?? j.job_id ?? j.JobID ?? undefined,
        title: j.title ?? j.name ?? j.JobTitle ?? '',
        level_id: j.level_id ?? j.LevelId ?? null,
        level_code: j.level_code ?? j.level ?? j.Level ?? null,
        active: (function toBool(v) {
          if (v === undefined || v === null) return true; // default active
          if (typeof v === 'boolean') return v;
          if (typeof v === 'number') return v !== 0;
          if (typeof v === 'string') return !(v.toLowerCase() === 'false' || v === '0' || v.toLowerCase() === 'inactive');
          return !!v;
        })(j.active ?? j.is_active ?? j.enabled ?? j.status)
      }));
      dispatch(settingsSlice.actions.setJobs(normalized));
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.setJobsError(error?.response?.data?.message || error.message));
    }
  };
}

export function upsertJob(job) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      const payload = {
        id: job.id ?? undefined,
        title: job.title,
        level_id: job.level_id ?? job.levelId ?? null,
        active: job.active === undefined ? undefined : !!job.active,
      };
      await axios.post(
        `${API_URL}/api/settings/jobs/upsert`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      dispatch(getJobs());
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error?.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

export function deleteJob(jobId) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      await axios.post(
        `${API_URL}/api/settings/jobs/delete`,
        { id: jobId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      dispatch(getJobs());
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error?.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

// Delete a single rule by id
export function deleteEmailRoutingRule(ruleId) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      await axios.post(
        `${API_URL}/api/settings/email-routing/delete`,
        { id: ruleId },
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(getEmailRouting());
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
    } catch (error) {
      console.error(error);
      dispatch(settingsSlice.actions.hasError(error.response?.data?.message || error.message));
      dispatch(settingsSlice.actions.setShowSettingsSuccess(false));
    }
  };
}

// Azure VM Integration - Send project to TSD application running in Azure VM
export function sendProjectToVm(projectNumber, userEmail) {
  const API_URL = process.env.REACT_APP_API_URL;
  return async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/settings/send-project-to-vm`,
        {
          project_number: projectNumber,
          user_email: userEmail
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      dispatch(settingsSlice.actions.setShowSettingsSuccess(true));
      return response?.data ?? { success: true };
    } catch (error) {
      console.error('Error sending project to VM:', error);
      dispatch(settingsSlice.actions.hasError(error?.response?.data?.message || error.message));
      throw error;
    }
  };
}

export default settingsSlice.reducer;